import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';
import { checkoutFunnelStep } from 'utils/Reserbus';
import getCardBrand from 'utils/cards';
import formErrorsTracker from 'metrics/user-analytics/formErrorsTracker';
import LoadingWallet from 'components/LoadingWallet';
import CheckoutNavigation from 'components/purchase/CheckoutNavigation';
import DiscountCode from 'components/purchase/DiscountCode';
import PromotionalCode from 'components/purchase/PromotionalCode';
import PaymentOptions from 'components/purchase/PaymentOptions';
import PaymentButton from 'components/PaymentButton';
import { shouldUseStripe, isStripeGooglePayEnabled } from 'utils/stripe';
import stripeEngine from 'payments/core/engines/stripe';
import OneSignal from 'react-onesignal';
import { postNativeIdentifyUser } from 'utils/webview';
import { identifyUser, trackEvent } from 'user-analytics';
import { Spacing, Text, Visibility } from '@reservamos/elements';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import adyen from 'payments/core/engines/adyen';
import basisTheoryCheckoutEngine from 'payments/core/engines/basisTheory';
import AgreementCheckboxFields from 'components/AgreementCheckboxFields';
import RedeemHandler from 'components/Wallet';
import DynamicPriceModal from 'ui/molecules/DynamicPriceModal';
import WelcomeDigitalDiscountModal from 'components/search/ModalContent/WelcomeDigitalDiscountModal';
import shouldShowWelcomeDigitalDiscount from 'utils/welcomeDigitalDiscountValidation';
import { getHasDynamicPricing } from 'utils/purchase/dynamicPricing';
import { isAddiBlockedByDocument } from 'utils/purchase/addiDocumentValidation';
import { selectCardCheckoutDecision } from 'selectors/cardCheckoutSelectors';
import wayIsOpenTicket, { isHybridTrip } from '../../../utils/wayIsOpenTicket';
import NotWsSandboxWarning from '../NotWsSandboxWarning';
import { MercadoPagoTDCContext } from '../../mercadoPago';
import OpenTicketMessage from '../OpenTicketMessage';
import CheckoutWallet from '../CheckoutWallet';
import SmallNotice from '../../../ui/atoms/SmallNotice';
import availablePaymentEngines from '../../../constants/PaymentEngine';
import InfoText from '../../../ui/atoms/InfoText';
import ReturnCTA from '../Purchase/ReturnCTA';
import AncillariesSwitch from '../../../ui/atoms/AncillariesSwitch';
import CostaPassSlider from '../../CostaPass/CostaPassSlider';
import WalletNotAvailable from '../../CostaPass/WalletNotAvailable';
import TpFundWidget from '../../TpFundWidget';
import PassengerPolicies from '../PassengerPolicies';
import DotersSlider from '../../Doters/DotersSlider/DotersSlider';
import SSOAuthDialog from '../../sso/SSOAuthDialog';
import DotersBanner from '../../../ui/atoms/DotersBanner';
import DotersModalOtp from '../../Doters/DotersModalOtp';
import DotersModalUsage from '../../Doters/DotersModalUsage/DotersModalUsage';
import BookingReview from '../../../ui/atoms/BookingReview';
import InsuranceSection from '../InsuranceSection';
import evertec from '../../../../payments/core/engines/evertec';
import PassengerSelectorBox from '../PassengerSelectorBox';
import { withCieloTDCContext } from '../../cielo/CieloTDCContext';
import ServiceLevels from '../ServiceLevels';
import { isPayPalV6Enabled } from '../../../utils/paypalV6Config';
import { isApplePayAvailable } from '../../../utils/applePay';
import { shouldUseAdyen, isAdyenGooglePayEnabled } from '../../../utils/adyen';
import { autoDisableInsuranceIfIneligible } from '../../../utils/insuranceEligibility';
import { withInsuranceEligibility } from '../../../hooks/useInsuranceEligibility';
import { getPurchaseProfileAnalyticsTraits } from '../../../utils/purchaseProfileAnalyticsTraits';

const propTypes = {
  purchase: PropTypes.shape({
    isUpdating: PropTypes.bool.isRequired,
    hasTickets: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    loaded: PropTypes.bool.isRequired,
    state: PropTypes.string.isRequired,
    departs: PropTypes.object.isRequired,
    returns: PropTypes.object.isRequired,
    availablePayments: PropTypes.array,
    availableWallets: PropTypes.array,
    roundTrip: PropTypes.bool,
    selectedInstallmentsPlan: PropTypes.shape({
      months: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
      monthlyPayment: PropTypes.number.isRequired,
    }),
    passengers: PropTypes.arrayOf(
      PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        phoneCode: PropTypes.string,
        phoneCountry: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    paymentEngine: PropTypes.string.isRequired,
    qualifiesForInsurance: PropTypes.bool.isRequired,
    selectedPaymentOption: PropTypes.string.isRequired,
    selectedPaymentMethod: PropTypes.object.isRequired,
    total: PropTypes.number.isRequired,
    updatingInsurance: PropTypes.bool.isRequired,
    updatingServiceLevel: PropTypes.bool.isRequired,
    applyingDiscount: PropTypes.bool.isRequired,
    wantsInsurance: PropTypes.bool.isRequired,
    openTicket: PropTypes.bool,
    isExchange: PropTypes.bool,
    email: PropTypes.string,
    paymentMethods: PropTypes.array,
    bulkTicket: PropTypes.bool,
    yuno: PropTypes.shape({
      checkoutSession: PropTypes.string,
      merchantOrderId: PropTypes.string,
      availableMethods: PropTypes.array,
    }),

    /**
     * Carbon ancillary props
     */
    wantsCarbonOffset: PropTypes.bool.isRequired,
    updatingCarbonOffset: PropTypes.bool.isRequired,
    updatingWallet: PropTypes.bool,
    walletPointsUsed: PropTypes.number,
    walletType: PropTypes.string,
    walletBalanceUsed: PropTypes.number,
    walletMinimumPointsToBeUsed: PropTypes.number,
    isUpdatingWalletType: PropTypes.bool,
    walletTotalPointsUsed: PropTypes.number,
    walletTotalBalanceUsed: PropTypes.number,
    fetchingTaxpayerId: PropTypes.bool,
  }),
  purchaser: PropTypes.shape({
    purchaserFirstName: PropTypes.string,
    purchaserLastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    phoneCountry: PropTypes.string,
    phoneCode: PropTypes.string,
    documentType: PropTypes.string,
    documentId: PropTypes.string,
  }),
  firstPassengerProfile: PropTypes.object,
  fetchingPayment: PropTypes.bool.isRequired,
  stripeSubmitting: PropTypes.bool,
  cardCheckoutMode: PropTypes.oneOf(['basis_theory', 'legacy']),
  paymentId: PropTypes.number,
  cardValues: PropTypes.object.isRequired,
  fetchCardList: PropTypes.func.isRequired,
  onToggleWantsInsurance: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  onFormFail: PropTypes.func.isRequired,
  onCardPaymentAttempt: PropTypes.func.isRequired,
  onPaymentAttempt: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  returnTo: PropTypes.func.isRequired,
  onSelectPaymentOption: PropTypes.func.isRequired,
  resetInstallmentsPlan: PropTypes.func.isRequired,
  insuranceMessage: PropTypes.string,
  insuranceAmount: PropTypes.string,
  insuranceAmountWithTaxes: PropTypes.string,
  insuranceAmountIsUnitPrice: PropTypes.bool,
  onTransferPaymentAttempt: PropTypes.func.isRequired,
  onThirdpartyPaymentAttempt: PropTypes.func.isRequired,
  purchaserUpdated: PropTypes.bool.isRequired,
  isPurchaserFormValid: PropTypes.bool,
  onSetPurchaser: PropTypes.func.isRequired,
  onStorePaymentAttempt: PropTypes.func.isRequired,
  cardErrorOccured: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool.isRequired,
  user: PropTypes.object,
  isLogged: PropTypes.bool.isRequired,
  showWalletNotAvailable: PropTypes.bool,
  showCdsFrequentPassengerOptIn: PropTypes.bool,
  cardsUpdated: PropTypes.bool,
  cardsLoading: PropTypes.bool,
  onAdyenPaymentAttempt: PropTypes.func.isRequired,
  selectedCard: PropTypes.object,
  monthlySelectedPlan: PropTypes.string.isRequired,
  resetPaymentCardError: PropTypes.func,
  features: PropTypes.object.isRequired,
  env: PropTypes.object.isRequired,
  paymentEngine: PropTypes.string,
  onEfectyPaymentAttempt: PropTypes.func.isRequired,
  onBNPLPaymentAttempt: PropTypes.func.isRequired,
  onWalletPaymentAttempt: PropTypes.func.isRequired,
  onEvertecPaymentAttempt: PropTypes.func.isRequired,
  onConfirmEvertecPayment: PropTypes.func.isRequired,
  onEvertecPaymentFailure: PropTypes.func.isRequired,
  onYunoPaymentAttempt: PropTypes.func.isRequired,
  fetchYunoData: PropTypes.func.isRequired,
  selectedPaymentMethod: PropTypes.object.isRequired,
  clearError: PropTypes.func,
  /**
   * Carbon ancillary props
   */
  carbonOffset: PropTypes.object,
  onToggleWantsCarbonOffset: PropTypes.func.isRequired,
  onNoCardSelected: PropTypes.func,
  otpTriggerPayment: PropTypes.bool,
  showOTPModal: PropTypes.bool,
  showRedeemModal: PropTypes.bool,
  loadingOTP: PropTypes.bool,
  clearOTP: PropTypes.func,
  generateOTP: PropTypes.func,
  otpConfirmed: PropTypes.bool,
  selectedLoyaltyProgram: PropTypes.string,
  isUserAuthenticated: PropTypes.bool,
  reserhubAccountEnabled: PropTypes.bool,
  saveBulkPassengers: PropTypes.func.isRequired,
  cieloTDCContext: PropTypes.object,
  redeemModalToggle: PropTypes.func,
  nitCodeValidated: PropTypes.bool,
  yapePaymentAttempt: PropTypes.func.isRequired,
  onPaypalPaymentAttempt: PropTypes.func.isRequired,
  insuranceEligibility: PropTypes.shape({
    hasIneligibleDoc: PropTypes.bool.isRequired,
    passengerDocs: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

/**
 * Checkout Component
 * Handles the display and interaction of the checkout process including payment options,
 * insurance selection, carbon offset option, and other ancillary services.
 * It manages state related to payment type, form submission, and OneSignal data for notifications.
 * The component also handles redirection based on user state and ticket availability,
 * OTP handling for Doters loyalty program, and rendering of various UI components
 * based on the features available and the user's choices.
 * When NOT_WS_SANDBOX is enabled, render a modal warning that purchases use production (no WS sandbox).
 * NOTE: some redirect paths that affect Checkout are handled inside Purchase.js component.
 */
class Checkout extends Component {
  constructor(props) {
    super(props);

    const { availablePayments } = props.purchase;

    this.state = {
      paymentType: availablePayments[0] || 'credit_card',
      paymentEngine: null,
      dotersUsageConfirmed: false,
      showDotersModal: false,
      isEvertecLoading: false,
      isYunoLoading: false,
      yapeFormData: null,
      yapeFormValid: false,
      isYapeSubmitting: false,
      notWsSandboxWarningDismissed: false,
      isSyncingPurchaserForWallet: false,
      basisTheoryStatus: basisTheoryCheckoutEngine.getStatus(),
    };

    /** Holds Adyen wallet resolve/reject while purchaser is saved before opening the payment sheet. */
    this.pendingWalletPaymentParams = null;

    this.setPaymentType = this.setPaymentType.bind(this);
    this.toggleWantsInsurance = this.toggleWantsInsurance.bind(this);
    this.handleFormErrors = this.handleFormErrors.bind(this);
    this.paymentButtonClick = this.paymentButtonClick.bind(this);
    this.cardPaymentAttempt = this.cardPaymentAttempt.bind(this);
    this.paymentAttempt = this.paymentAttempt.bind(this);
    this.onSubmitTransferForm = this.onSubmitTransferForm.bind(this);
    this.onSubmitPurchaser = this.onSubmitPurchaser.bind(this);
    this.handlePurchaserSubmitFail = this.handlePurchaserSubmitFail.bind(this);
    this.finishWalletPurchaserSync = this.finishWalletPurchaserSync.bind(this);
    this.clearWalletPurchaserSync = this.clearWalletPurchaserSync.bind(this);
    this.toggleWantsCarbonOffset = this.toggleWantsCarbonOffset.bind(this);
    this.needsConfirmation = this.needsConfirmation.bind(this);
    this.handleDotersUsageModalButtonClick = this.handleDotersUsageModalButtonClick.bind(this);
    this.handleEvertecPayment = this.handleEvertecPayment.bind(this);
    this.handleYunoPayment = this.handleYunoPayment.bind(this);
    this.handleYapeFormDataChange = this.handleYapeFormDataChange.bind(this);
    this.handleYapeValidationChange = this.handleYapeValidationChange.bind(this);
  }

  componentDidMount() {
    const { resetPaymentCardError, clearOTP } = this.props;

    resetPaymentCardError();
    this.redirectIfAppropriate();
    this.getYunoAvailableMethods();
    this.unsubscribeBasisTheoryStatus = basisTheoryCheckoutEngine.subscribe((status) => {
      this.setState({ basisTheoryStatus: status });
    });
    clearOTP();

    // Auto-disable insurance on initial load if there are ineligible documents
    const { features, onToggleWantsInsurance, purchase, insuranceEligibility } = this.props;
    autoDisableInsuranceIfIneligible({
      purchase,
      features,
      onToggleWantsInsurance,
      getInsuranceEligibility: () => insuranceEligibility,
    });
  }

  componentDidUpdate(prevProps) {
    const {
      transitionTo,
      fetchingPayment,
      purchase,
      paymentId,
      user,
      fetchCardList,
      cardsUpdated,
      cardsLoading,
      features,
      otpTriggerPayment,
    } = this.props;

    this.redirectIfAppropriate(prevProps);

    const { total, availablePayments } = purchase;

    if (
      (prevProps.purchase.selectedPaymentOption === 'reservamos_pay' ||
        prevProps.purchase.selectedPaymentOption === 'free_pay') &&
      total &&
      availablePayments.length
    ) {
      this.setPaymentType(availablePayments[0] || 'credit_card');
    }
    if (prevProps.fetchingPayment && !fetchingPayment) {
      // go to payment processor after fetching the payment
      transitionTo(`/payment/${purchase.token}/${paymentId}`);
    }

    if (features.ALLOW_TO_SAVE_CARDS && !cardsUpdated && !cardsLoading) {
      if (user) {
        fetchCardList();
      }
    }

    const purchaserFormInCheckoutStep = features.PURCHASER_FORM_ON === 'CHECKOUT';
    if (purchase.isUpdating && purchaserFormInCheckoutStep) {
      window.scrollTo({ top: 0 });
    }

    // If the otp has already been validated, the payment is triggered
    if (features.USE_OTP && !fetchingPayment && otpTriggerPayment && !prevProps.otpTriggerPayment) {
      this.paymentButtonClick();
    }

    // Auto-disable insurance if any passenger has an ineligible document type
    const { onToggleWantsInsurance, insuranceEligibility } = this.props;
    autoDisableInsuranceIfIneligible({
      purchase,
      features,
      onToggleWantsInsurance,
      getInsuranceEligibility: () => insuranceEligibility,
    });
  }

  componentWillUnmount() {
    const { resetInstallmentsPlan } = this.props;

    if (this.pendingWalletPaymentParams?.reject) {
      this.pendingWalletPaymentParams.reject();
    }
    this.pendingWalletPaymentParams = null;
    if (this.unsubscribeBasisTheoryStatus) this.unsubscribeBasisTheoryStatus();
    resetInstallmentsPlan();
  }

  handleDismissNotWsSandboxWarning = () => {
    this.setState({ notWsSandboxWarningDismissed: true });
  };

  handleFormErrors(fields) {
    const { paymentType } = this.state;
    // prettier-ignore
    const {
      onFormFail,
      purchase: { departs, returns },
      cardValues: { cardNumber },
    } = this.props;

    const paymentInfo = {
      paymentType,
    };

    if (cardNumber) {
      paymentInfo.cardBrand = getCardBrand(cardNumber);
    }

    if (fields) {
      formErrorsTracker(checkoutFunnelStep(departs, returns), fields, paymentInfo);
      onFormFail();
    }
  }

  handleDotersUsageModalButtonClick(status) {
    this.setState({ showDotersModal: false });
    trackEvent('Doters Points Confirmed', { accepted: status });
    if (status) {
      this.setState({ dotersUsageConfirmed: status }, () => this.paymentButtonClick());
    }
  }

  async handleEvertecPayment() {
    this.setState({ isEvertecLoading: true });
    const {
      env: {
        api: { purchaseUrl },
        brand,
        currency,
      },
      purchase: {
        token,
        total: amount,
        selectedPaymentMethod: { type, provider },
      },
      purchaser,
      onEvertecPaymentAttempt,
      onConfirmEvertecPayment,
      onEvertecPaymentFailure,
    } = this.props;

    const description = 'ecommerce & apps for bus tickets';
    const returnUrl = `${window.location.origin}/purchase/${token}/complete`;
    const cancelUrl = `${window.location.origin}/payment/${token}/canceled`;

    const resp = await evertec.createSession({
      purchaseUrl,
      brand,
      purchaseId: token,
      description,
      currency,
      amount,
      returnUrl,
      cancelUrl,
      paymentType: type,
      purchaser,
    });

    if (!resp || resp.status === 'failure') {
      this.setState({ isEvertecLoading: false });
      return onEvertecPaymentFailure();
    }

    const { process_url: processUrl, request_id: requestId } = resp;

    try {
      await onEvertecPaymentAttempt({ purchaseToken: token, type, provider, requestId });
    } catch {
      return;
    } finally {
      this.setState({ isEvertecLoading: false });
    }

    if (type === 'transfer') {
      window.location.href = processUrl;
    } else {
      try {
        await evertec.openLightbox({
          processUrl,
          onRejected: () => {
            window.location.href = `/payment/${token}/canceled`;
          },
          onApproved: onConfirmEvertecPayment,
          onClose: () => {
            this.setState({ isEvertecLoading: false });
          },
        });
      } catch {
        this.setState({ isEvertecLoading: false });
        onEvertecPaymentFailure();
      }
    }
  }

  async handleYunoPayment() {
    this.setState({ isYunoLoading: true });
    const {
      purchase: {
        selectedPaymentMethod,
        token: purchaseToken,
        yuno: { checkoutSession, merchantOrderId },
      },
      onYunoPaymentAttempt,
    } = this.props;
    try {
      await onYunoPaymentAttempt({
        checkoutSession,
        merchantOrderId,
        selectedPaymentMethod,
        purchaseToken,
      });
    } catch (error) {
      throw new Error(`Failed to process Yuno payment: ${error.message}`);
    } finally {
      this.setState({ isYunoLoading: false });
    }
  }

  handleYapePayment = async () => {
    const { yapePaymentAttempt, onFormFail } = this.props;
    const { yapeFormData, yapeFormValid } = this.state;

    if (!yapeFormData || !yapeFormValid) {
      onFormFail();
      return;
    }

    const { isYapeSubmitting } = this.state;
    if (isYapeSubmitting) {
      return;
    }

    this.setState({ isYapeSubmitting: true });
    try {
      await yapePaymentAttempt(yapeFormData);
    } catch (error) {
      onFormFail();
    } finally {
      this.setState({ isYapeSubmitting: false });
    }
  };

  handleYapeFormDataChange = (formData) => {
    this.setState({ yapeFormData: formData });
  };

  handleYapeValidationChange = (isValid) => {
    this.setState({ yapeFormValid: isValid });
  };

  handlePurchaserSubmitFail() {
    const pendingWallet = this.pendingWalletPaymentParams;
    this.pendingWalletPaymentParams = null;
    this.clearWalletPurchaserSync();

    // Abort the wallet payment when purchaser validation fails. The sheet has not opened yet
    // (resolve never ran), so this closes it where the engine supports it (Stripe ECE) and is a
    // no-op abort for Adyen (whose reject is neutralized in its hook to avoid unhandled rejections).
    if (pendingWallet?.reject) {
      pendingWallet.reject();
    }
  }

  onSubmitTransferForm(fields) {
    const { onTransferPaymentAttempt } = this.props;
    onTransferPaymentAttempt(fields);
  }

  onSubmitPurchaser() {
    const { onSetPurchaser, features, showCdsFrequentPassengerOptIn } = this.props;
    const pendingWallet = this.pendingWalletPaymentParams;
    this.pendingWalletPaymentParams = null;

    onSetPurchaser()
      .then(() => {
        // skipPurchaserCheck avoids a loop when Redux has not re-rendered yet after save:
        // - SHOW_PROMOTIONAL_TERMS_CHECKBOX / CDS_FREQUENT_PASSENGER_ENABLED: meta.accepted_promotional_terms
        //   / meta.frequent_passenger_enrollment may lag vs form
        // - wallet flow: purchase purchaser fields may lag vs form until RECEIVE_PURCHASER propagates
        const skipPurchaserCheck =
          Boolean(features?.SHOW_PROMOTIONAL_TERMS_CHECKBOX) ||
          Boolean(showCdsFrequentPassengerOptIn) ||
          Boolean(pendingWallet);

        const continuePaymentParams = { skipPurchaserCheck };
        if (pendingWallet) {
          continuePaymentParams.resolve = pendingWallet.resolve;
          continuePaymentParams.reject = pendingWallet.reject;
          // setPurchaser's promise resolves after RECEIVE_PURCHASER dispatches,
          // so isUpdating is already false here — safe to continue immediately.
          this.finishWalletPurchaserSync(continuePaymentParams);
          return;
        }

        this.paymentButtonClick(continuePaymentParams);
      })
      .catch(() => {
        this.clearWalletPurchaserSync();
        if (pendingWallet?.reject) {
          pendingWallet.reject();
        }
      });
  }

  async getYunoAvailableMethods() {
    const {
      env: {
        api: { purchaseUrl },
        yuno,
      },
      purchase: { token: purchaseToken, total: amount },
      fetchYunoData,
    } = this.props;
    if (!yuno?.enabled || !amount || amount <= 0) return null;

    try {
      await fetchYunoData({ purchaseUrl, purchaseToken, amount });
    } catch (error) {
      throw new Error(`Error in getYunoAvailableMethods: ${error}`);
    }
  }

  setPaymentType(paymentType, paymentMethod) {
    if (paymentMethod?.provider === 'yuno') {
      paymentMethod = {
        provider: paymentMethod.provider,
        engine: paymentMethod.engine,
        type: paymentMethod.type,
      };
    }
    const { onSelectPaymentOption } = this.props;

    this.setState({ paymentType, paymentEngine: paymentMethod?.engine });
    onSelectPaymentOption(paymentType, paymentMethod);
  }

  setOneSignalData() {
    const { user, purchaser, purchase, env, isUserAuthenticated, firstPassengerProfile } =
      this.props;
    const storedPassenger = purchase.passengers && purchase.passengers[0];
    const hasOneSignalEnabled = env.oneSignal.enabled;
    const userEmail = purchaser.email || purchase.email;
    const phoneNumber = parsePhoneNumber(
      `+${getCountryCallingCode(purchaser.phoneCountry)}${purchaser.phone}`,
    );
    if (hasOneSignalEnabled) {
      OneSignal.setSMSNumber(`${phoneNumber.number}`);
      OneSignal.setExternalUserId(phoneNumber.nationalNumber);
      OneSignal.setEmail(userEmail);
      OneSignal.sendTags({ purchase_token: purchase.token });
    }
    identifyUser(phoneNumber.nationalNumber, {
      '$first_name': purchaser.purchaserFirstName,
      '$last_name': purchaser.purchaserLastName,
      '$name': `${purchaser.purchaserFirstName} ${purchaser.purchaserLastName}`,
      '$email': userEmail,
      '$phone': phoneNumber.nationalNumber,
      'User Type': isUserAuthenticated ? 'Registered' : 'Anonymous',
      '$onesignal_user_id': phoneNumber.nationalNumber,
      ...(user ? { $membership_id: user.spCard || user.dotersId } : {}),
      ...getPurchaseProfileAnalyticsTraits(env.brand, {
        passenger: storedPassenger,
        passengerFromForm: firstPassengerProfile,
        purchaser,
      }),
    });
    postNativeIdentifyUser({
      phone: phoneNumber.nationalNumber,
      email: userEmail,
      firstName: purchaser.purchaserFirstName,
      lastName: purchaser.purchaserLastName,
    });
  }

  getWalletPointsText() {
    const { t, purchase, env } = this.props;
    const { travelpassLoyaltyConfig } = env;
    const prefix = travelpassLoyaltyConfig?.prefix || 'costapass';
    const { walletType } = purchase;
    const types = {
      doters: 'payment:label.pay_with_doter_points',
      costapass: 'payment:label.pay_with',
      travelpass: 'payment:label.pay_with',
      siempreplus: 'payment:label.pay_with_kilometres',
    };
    return t(types[walletType], { context: prefix });
  }

  clearWalletPurchaserSync() {
    this.setState({ isSyncingPurchaserForWallet: false });
  }

  finishWalletPurchaserSync(continuePaymentParams) {
    this.setState({ isSyncingPurchaserForWallet: false }, () => {
      this.paymentButtonClick(continuePaymentParams);
    });
  }

  toggleWantsCarbonOffset(event) {
    const {
      purchase: { token },
      onToggleWantsCarbonOffset,
    } = this.props;

    onToggleWantsCarbonOffset(token, event.target.checked);
  }

  toggleWantsInsurance(event) {
    const {
      purchase: { token },
      onToggleWantsInsurance,
    } = this.props;

    onToggleWantsInsurance(token, event.target.checked);
  }

  redirectIfAppropriate(prevProps) {
    const { purchase, returnTo, isLogged, features } = this.props;
    const { hasTickets, passengers, token, bulkTicket: isBulkTicket, isExchange } = purchase;
    const hasPassengers = passengers.length > 0;

    if (isBulkTicket) return;

    // Use previous props if given
    if (prevProps) {
      // If state has changed from not logged to logged
      // then we redirect back to passengers page to update tickets
      if (!prevProps.isLogged && isLogged) {
        returnTo(`/purchase/${token}/passengers`);
        return;
      }
    }

    // For CDS exchanges (V2), skip this check as tickets are created after the purchase
    // Only skip if EXCHANGE_VALIDATE_ELIGIBILITY_BEFORE_SCHEDULE is enabled (CDS specific)
    if (isExchange && features.EXCHANGE_VALIDATE_ELIGIBILITY_BEFORE_SCHEDULE) {
      return;
    }

    if (!(hasTickets && hasPassengers)) {
      returnTo(`/purchase/${token}/passengers`);
    }
  }

  needsConfirmation() {
    const {
      purchase,
      features,
      otpConfirmed,
      nitCodeValidated,
      generateOTP,
      user,
      redeemModalToggle,
    } = this.props;
    const { walletPointsUsed, walletMinimumPointsToBeUsed, walletType, walletBalanceUsed } =
      purchase;
    const { dotersUsageConfirmed } = this.state;
    const hasUsedPoints = walletPointsUsed || walletMinimumPointsToBeUsed;

    // If either OTP or confirmation modal has been accepted, return false
    if (otpConfirmed || dotersUsageConfirmed) return false;

    if (walletType === 'doters' && user?.email && hasUsedPoints) {
      if (features.USE_OTP) {
        // Confirmation happens in OTP modal
        generateOTP(parseFloat(walletPointsUsed || walletMinimumPointsToBeUsed));
        return true;
      }

      // Confirmation happens in prompt modal
      this.setState({ showDotersModal: true });
      return true;
    }

    if (walletType === 'travelpass' && walletBalanceUsed > 0 && !nitCodeValidated) {
      if (features.USE_OTP_FOR_WALLET) {
        redeemModalToggle(true);
        return true;
      }
    }
    return false;
  }

  paymentButtonClick(paymentParams = {}) {
    const {
      onSubmitForm,
      purchaserUpdated,
      isPurchaserFormValid,
      onSetPurchaser,
      onStorePaymentAttempt,
      purchase: { isUpdating, selectedPaymentOption },
      onAdyenPaymentAttempt,
      fetchingPayment,
      selectedCard,
      paymentEngine,
      features,
      env,
      onEfectyPaymentAttempt,
      onBNPLPaymentAttempt,
      onWalletPaymentAttempt,
      selectedPaymentMethod,
      clearError,
      onNoCardSelected,
      saveBulkPassengers,
      onFormFail,
      purchaser,
      purchase,
      cieloTDCContext,
      onPaypalPaymentAttempt,
      cardCheckoutMode,
    } = this.props;
    const { token, bulkTicket, passengers } = purchase;

    if (isUpdating || fetchingPayment) return;

    // Addi (Yuno BNPL) only accepts Cédula de Ciudadanía. The disabled button
    // guards the rendered click, but paymentButtonClick can also be re-entered
    // programmatically (e.g. onSubmitPurchaser resume with skipPurchaserCheck),
    // so block the payment here too when the buyer document is not accepted.
    if (
      isAddiBlockedByDocument({
        selectedPaymentMethod,
        purchaserDocumentType: purchaser?.documentType,
      })
    ) {
      paymentParams.reject?.();
      return;
    }

    const showPurchaserForm = features.PURCHASER_FORM_ON === 'CHECKOUT';

    const { submitForm: submitMercadoPagoTDCForm } = this.context;
    const { submitForm: submitCieloTDCForm } = cieloTDCContext;
    const mercadoPagoIsEnabled = env.mercadoPago.enabled;
    const mercadoPagoCreditCardIsEnabled = env.mercadoPago?.creditCard?.enabled ?? false;
    const shouldUseMercadoPago =
      mercadoPagoIsEnabled && paymentEngine === availablePaymentEngines.mercadoPago;
    const cieloHasActiveFlow = env.cielo?.enableThreeDS || env.cielo?.enableSilentOrderPost;
    const shouldUseCieloTDC =
      selectedPaymentMethod.type === 'credit_card' &&
      selectedPaymentMethod.engine === 'cielo' &&
      cieloHasActiveFlow;
    const shouldUseEvertec = selectedPaymentMethod.engine === 'evertec';
    const shouldUseYuno = selectedPaymentMethod.provider === 'yuno';

    const { provider } = selectedPaymentMethod || {};

    if (this.needsConfirmation()) return;

    const isWalletPayment =
      selectedPaymentOption === 'apple_pay' || selectedPaymentOption === 'google_pay';

    // A wallet sheet (Apple Pay / Google Pay) must never open on an invalid purchaser form.
    // `purchaserUpdated` only means "no unsaved diff vs the saved purchase", not validity: it can be
    // true while the form is actually invalid (e.g. an unparseable phone normalizes to
    // `undefined === undefined`, or a required field left untouched), which would let the sheet open
    // unvalidated. So gate sheet opening on client-side validity here, independent of
    // `purchaserUpdated` and before the unsaved-changes handling below. Only block on a positive
    // invalid result so a missing prop preserves prior behavior.
    if (
      showPurchaserForm &&
      isWalletPayment &&
      isPurchaserFormValid === false &&
      !paymentParams.skipPurchaserCheck
    ) {
      if (paymentParams.reject) {
        paymentParams.reject();
      }
      onSubmitForm('purchaser');
      onFormFail();
      return;
    }

    // Adyen Google Pay opens the sheet only after resolve() runs, so the purchaser PUT can be
    // deferred across it: save first, then resolve afterwards (see onSubmitPurchaser). Apple Pay
    // cannot do this — its ApplePaySession must be created within the original click gesture — so
    // it uses the synchronous-resolve + registerPurchaserSave path below instead.
    const shouldSyncPurchaserBeforeWallet =
      showPurchaserForm &&
      !purchaserUpdated &&
      !paymentParams.skipPurchaserCheck &&
      isWalletPayment &&
      typeof paymentParams.resolve === 'function' &&
      typeof paymentParams.reject === 'function';

    // If purchaser form is enabled but not yet updated, save it before paying.
    // skipPurchaserCheck: true when coming from onSubmitPurchaser success (avoids loop if Redux hasn't re-rendered).
    if (showPurchaserForm && !purchaserUpdated && !paymentParams.skipPurchaserCheck) {
      // Wallet validity is already enforced by the gate above, so a wallet reaching here is valid.
      if (typeof paymentParams.registerPurchaserSave === 'function') {
        // Stripe Google Pay and Adyen Apple Pay must open the wallet sheet synchronously on click;
        // the user gesture is lost across an async purchaser PUT (Apple Pay throws "Must create a
        // new ApplePaySession from a user gesture handler", and the re-trigger after
        // onSubmitPurchaser carries no resolve). Open the sheet now and defer the purchaser PUT to
        // the confirm step via registerPurchaserSave (see useStripeGooglePay#handleConfirm and
        // useAdyenApplePay#handleSubmit).
        paymentParams.registerPurchaserSave(() => onSetPurchaser());
        // Falls through to processPayment → resolve() opens the sheet; the PUT runs on confirm.
      } else if (shouldSyncPurchaserBeforeWallet) {
        // Adyen Google Pay: save purchaser first, then call resolve() to open the sheet.
        this.pendingWalletPaymentParams = {
          resolve: paymentParams.resolve,
          reject: paymentParams.reject,
        };
        this.setState({ isSyncingPurchaserForWallet: true });
        onSubmitForm('purchaser');
        return;
      } else {
        // Non-wallet purchaser form (e.g. credit card): submit and save before paying.
        onSubmitForm('purchaser');
        onFormFail();
        return;
      }
    }

    /**
     * Processes payment after validating all necessary conditions.
     * Handles different payment methods like Adyen, MercadoPago, Evertec, etc.
     */
    const processPayment = () => {
      clearError();

      if (showPurchaserForm) {
        this.setOneSignalData();
      }

      // when availablePayments comes as an objects array, selectedPaymentOption will be an object too
      // we would know what action to trigger using the selectedPaymentOption.type property
      // we would also have available the engine and provider to pass to the action
      // if (typeof selectedPaymentOption === 'object') {
      if (selectedPaymentOption === 'credit_card') {
        if (cardCheckoutMode === 'basis_theory') {
          basisTheoryCheckoutEngine.submit();
        } else if (shouldUseAdyen(paymentEngine)) {
          if (selectedCard) {
            onAdyenPaymentAttempt(selectedCard);
          } else {
            const adyenInstance = adyen.getAdyenInstance();
            if (!adyenInstance) {
              onNoCardSelected();
              return;
            }
            adyenInstance.submit();
          }
        } else if (shouldUseMercadoPago && mercadoPagoCreditCardIsEnabled) {
          submitMercadoPagoTDCForm();
        } else if (shouldUseEvertec) {
          this.handleEvertecPayment();
        } else if (shouldUseCieloTDC) {
          // Use Cielo form for both 3DS and SOP flows
          submitCieloTDCForm();
        } else if (shouldUseStripe(paymentEngine)) {
          stripeEngine.submit();
        } else {
          onSubmitForm('card');
        }
      } else if (shouldUseYuno) {
        this.handleYunoPayment();
      } else if (selectedPaymentOption === 'transfer') {
        if (provider === 'pix' || provider === 'nequi') {
          this.onSubmitTransferForm({});
        } else if (shouldUseEvertec) {
          this.handleEvertecPayment();
        } else {
          onSubmitForm('transfer');
        }
      } else if (selectedPaymentOption === 'store') {
        onStorePaymentAttempt();
      } else if (selectedPaymentOption === 'pix') {
        this.onSubmitTransferForm({ isLegacyPix: true });
      } else if (selectedPaymentOption === 'efecty') {
        onEfectyPaymentAttempt();
      } else if (selectedPaymentOption === 'bnpl') {
        onBNPLPaymentAttempt();
      } else if (selectedPaymentOption === 'otp') {
        this.handleYapePayment();
      } else if (selectedPaymentOption === 'wallet') {
        onWalletPaymentAttempt();
      } else if (selectedPaymentOption === 'paypal' && isPayPalV6Enabled()) {
        const { paypalType, paypalOptions = {} } = paymentParams;
        onPaypalPaymentAttempt(paypalType, paypalOptions);
      } else if (selectedPaymentOption === 'apple_pay') {
        if (paymentParams.resolve) {
          paymentParams.resolve();
        } else {
          console.warn('Apple Pay: paymentParams.resolve is missing');
        }
      } else if (selectedPaymentOption === 'google_pay') {
        if (paymentParams.resolve) {
          paymentParams.resolve();
        } else {
          console.warn('Google Pay: paymentParams.resolve is missing');
        }
      } else {
        this.paymentAttempt();
      }
    };

    // Special handling for bulk tickets
    if (bulkTicket) {
      const { email, phone, phoneCode, phoneCountry, purchaserFirstName, purchaserLastName } =
        purchaser;

      if (passengers.length === 0) {
        // Reject Apple Pay / Google Pay sheet if open before showing form error
        if (
          (selectedPaymentOption === 'apple_pay' || selectedPaymentOption === 'google_pay') &&
          paymentParams.reject
        ) {
          paymentParams.reject();
        }
        onFormFail();
        return;
      }

      // Format passengers with purchaser data
      const passengersPayload = passengers.map((passenger) => ({
        ...passenger,
        lastName: purchaserLastName,
        firstName: purchaserFirstName,
        email,
        phone,
        phoneCode,
        phoneCountry,
        name: `${purchaserFirstName} ${purchaserLastName}`,
      }));

      // Wallets that open their sheet within the click gesture (Apple Pay, Stripe Google Pay)
      // cannot let the bulk-passenger PUT block resolve() — deferring it would lose the gesture and
      // throw "Must create a new ApplePaySession from a user gesture handler". Defer the passenger
      // PUT to the wallet confirm step via registerPurchaserSave (composed with any purchaser save
      // the gate registered above) and open the sheet synchronously. Adyen Google Pay does not
      // register this callback and keeps the async path below (its sheet tolerates the deferral).
      if (isWalletPayment && typeof paymentParams.registerPurchaserSave === 'function') {
        const needsPurchaserSave =
          showPurchaserForm && !purchaserUpdated && !paymentParams.skipPurchaserCheck;
        paymentParams.registerPurchaserSave(async () => {
          if (needsPurchaserSave) await onSetPurchaser();
          await saveBulkPassengers(token, passengersPayload);
        });
        processPayment();
        return;
      }

      // First update the purchaser
      saveBulkPassengers(token, passengersPayload)
        .then(() => {
          // Once both operations are complete, proceed with payment
          processPayment();
        })
        .catch((error) => {
          console.error('Error in bulk ticket flow:', error);
          // Reject Apple Pay / Google Pay sheet if open before showing form error
          if (
            (selectedPaymentOption === 'apple_pay' || selectedPaymentOption === 'google_pay') &&
            paymentParams.reject
          ) {
            paymentParams.reject();
          }
          onFormFail();
        });
      return;
    }

    // If all validations pass, process the payment
    processPayment();
  }

  // This function is passed to PaymentOptions component
  // to use in the CardForm component when don't use Adyen or MercadoPago
  // like CyberSource for make the payment attempt
  cardPaymentAttempt() {
    const {
      purchase: { token, total: amount },
      onCardPaymentAttempt,
      cardValues,
      env: { currency },
    } = this.props;

    const paymentInfo = {
      amount,
      currency,
    };

    onCardPaymentAttempt(token, cardValues, paymentInfo);
  }

  paymentAttempt() {
    const {
      purchase,
      onPaymentAttempt,
      onThirdpartyPaymentAttempt,
      purchase: { selectedPaymentOption, selectedPaymentMethod },
    } = this.props;
    if (selectedPaymentOption === 'third_party') {
      onThirdpartyPaymentAttempt(purchase.token, selectedPaymentMethod);
    } else {
      onPaymentAttempt(purchase.token, selectedPaymentOption);
    }
  }

  render() {
    const {
      fetchingPayment,
      stripeSubmitting = false,
      purchase: {
        isUpdating,
        isExchange,
        total,
        wantsInsurance,
        availablePayments,
        paymentMethods,
        qualifiesForInsurance,
        updatingInsurance,
        updatingServiceLevel,
        applyingDiscount,
        selectedPaymentOption,
        selectedInstallmentsPlan,
        openTicket,
        departs,
        returns,
        updatingCarbonOffset,
        wantsCarbonOffset,
        updatingWallet,
        walletType,
        walletPointsUsed,
        walletTotalPointsUsed,
        walletBalanceUsed,
        walletMinimumPointsToBeUsed,
        walletTotalBalanceUsed,
        isUpdatingWalletType,
        passengers,
        bulkTicket,
        fetchingTaxpayerId,
      },
      insuranceMessage,
      insuranceAmount,
      insuranceAmountWithTaxes,
      insuranceAmountIsUnitPrice,
      cardErrorOccured,
      t,
      user,
      reserhubAccountEnabled,
      monthlySelectedPlan,
      features,
      env,
      carbonOffset,
      showOTPModal,
      loadingOTP,
      showRedeemModal,
      selectedLoyaltyProgram,
      cieloTDCContext: { isLoading: cieloTDCFormIsLoading },
      showWalletNotAvailable,
      showCdsFrequentPassengerOptIn,
      onSubmitForm,
      purchaserUpdated,
      insuranceEligibility,
      selectedPaymentMethod,
      paymentEngine: purchasePaymentEngine,
      purchaser,
      cardCheckoutMode,
    } = this.props;

    const {
      showDotersModal,
      isEvertecLoading,
      isYunoLoading,
      isYapeSubmitting,
      notWsSandboxWarningDismissed,
      basisTheoryStatus,
    } = this.state;

    const { paymentType: localPaymentType, paymentEngine: localPaymentEngine } = this.state;
    const paymentType = selectedPaymentOption || localPaymentType;
    const paymentEngine =
      localPaymentEngine || selectedPaymentMethod?.engine || purchasePaymentEngine;

    const usesBasisTheoryCheckout = cardCheckoutMode === 'basis_theory';
    const showInstallments = paymentType === 'credit_card' && monthlySelectedPlan > 1;
    const showLegacyInstallmentNotice = showInstallments && !usesBasisTheoryCheckout;
    let selectedPaymentTotal = total;

    if (showInstallments) {
      selectedPaymentTotal = selectedInstallmentsPlan.total;
    }

    const { isSyncingPurchaserForWallet } = this.state;
    const isLoading =
      isUpdating || fetchingPayment || isUpdatingWalletType || isSyncingPurchaserForWallet;

    const installmentNotice = <SmallNotice selectedInstallmentsPlan={selectedInstallmentsPlan} />;

    const shouldRenderLoading =
      (isLoading && !env.adyen.enabled) || (isLoading && isExchange) || isSyncingPurchaserForWallet;

    const purchaserFormInCheckoutStep = features.PURCHASER_FORM_ON === 'CHECKOUT';

    // Checking if one of the trips is open ticket to notify that the insurance is not available for open ticket
    const isHybrid = isHybridTrip(departs, returns);
    const showMessageNotInsuranceOpenTicket = isHybrid && !features.OPENT_TICKET_INSURANCE_ENABLED;
    const enablesCarbon = carbonOffset && carbonOffset.available;
    const enablesInsurance =
      ((isExchange && features.CAN_ADD_EXCHANGE_INSURANCE) || !isExchange) &&
      qualifiesForInsurance &&
      !features.PASSENGERS_INSURANCE_ENABLED;

    const { hasIneligibleDoc } = enablesInsurance ? insuranceEligibility : {};
    const disableInsuranceSwitch = hasIneligibleDoc;

    const buttonLoading =
      isLoading ||
      loadingOTP ||
      updatingWallet ||
      isEvertecLoading ||
      fetchingTaxpayerId ||
      isYunoLoading ||
      showRedeemModal ||
      cieloTDCFormIsLoading ||
      updatingServiceLevel ||
      isYapeSubmitting ||
      stripeSubmitting;
    const basisTheoryLoading =
      usesBasisTheoryCheckout &&
      (basisTheoryStatus === 'loading' || basisTheoryStatus === 'submitting');
    const basisTheoryUnavailable = usesBasisTheoryCheckout && basisTheoryStatus !== 'ready';
    const buttonLoadingWithCardCheckout = buttonLoading || basisTheoryLoading;
    const isPointsPaying =
      (walletPointsUsed || walletBalanceUsed || walletMinimumPointsToBeUsed) && total === 0;

    // Mirror DiscountCode's own visibility logic so the "additional services" gate
    // doesn't keep the section (and its title) mounted when the coupon input is suppressed.
    const departDiscountType = departs?.fragments?.[0]?.pricing?.discountType;
    const returnDiscountType = returns?.fragments?.[0]?.pricing?.discountType;
    const hasDynamicPricing = getHasDynamicPricing(departDiscountType, returnDiscountType);
    const hasDiscountCode =
      features.DISCOUNT_CODE_ENABLED &&
      !(hasDynamicPricing && features.DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT);

    const showWelcomeDigitalDiscountBanner = shouldShowWelcomeDigitalDiscount({
      originCityName: departs.origin?.cityName,
      destinationCityName: departs.destination?.cityName,
    });

    const displayCostapassSlider =
      selectedLoyaltyProgram === 'costapass' || selectedLoyaltyProgram === 'travelpass';
    const dotersEnabled = features.DOTERS_ENABLED;
    const dotersIsActiveForPurchase = dotersEnabled && walletType === 'doters';
    // Single source of truth for the Doters banner: it renders null for logged-in users
    // (show={!user}) and when Reserhub unified account is enabled (LoyaltyWidget returns null).
    // Keep this in sync with the <DotersBanner show={...}> prop below so the "additional
    // services" gate never mounts an empty section title.
    const displayDotersBanner =
      dotersEnabled && !dotersIsActiveForPurchase && !user && !reserhubAccountEnabled;
    const displayDotersSlider = dotersIsActiveForPurchase;
    const showDotersPointsEarned = dotersIsActiveForPurchase;
    const showNotWsSandboxWarning = features.NOT_WS_SANDBOX && !notWsSandboxWarningDismissed;
    const hideServices =
      !enablesInsurance &&
      !showMessageNotInsuranceOpenTicket &&
      !enablesCarbon &&
      !isExchange &&
      !displayCostapassSlider &&
      !displayDotersBanner &&
      !displayDotersSlider &&
      !showDotersPointsEarned &&
      !hasDiscountCode;
    const onlyDiscount =
      !enablesInsurance &&
      !showMessageNotInsuranceOpenTicket &&
      !enablesCarbon &&
      !isExchange &&
      !displayCostapassSlider &&
      !displayDotersBanner &&
      !displayDotersSlider &&
      !showDotersPointsEarned &&
      hasDiscountCode;

    const showAgreementCheckboxFields =
      features.SHOW_LEGAL_TERMS_CHECKBOX ||
      features.SHOW_PROMOTIONAL_TERMS_CHECKBOX ||
      showCdsFrequentPassengerOptIn;

    // Addi (Yuno BNPL) only accepts Cédula de Ciudadanía; block the pay button
    // when the buyer chose an unsupported document type (YunoForm shows the why).
    const addiBlocked = isAddiBlockedByDocument({
      selectedPaymentMethod,
      purchaserDocumentType: purchaser?.documentType,
    });
    const disabledPaymentButton =
      updatingInsurance ||
      applyingDiscount ||
      buttonLoadingWithCardCheckout ||
      basisTheoryUnavailable ||
      addiBlocked;

    // Hide main button if PayPal v6, Apple Pay, or Google Pay is selected - buttons are in their respective forms
    const shouldHideMainPaymentButton =
      (paymentType === 'paypal' && isPayPalV6Enabled()) ||
      (paymentType === 'apple_pay' && isApplePayAvailable()) ||
      (paymentType === 'google_pay' && (isAdyenGooglePayEnabled() || isStripeGooglePayEnabled()));

    const servicesComponents = (
      <Spacing vertical>
        <Text weight="bold" size="XL">
          {t('purchase:label.additional_services')}
        </Text>
        <Spacing vertical size="S">
          <DotersBanner show={displayDotersBanner} onPayment />
          <DotersSlider />
          <CostaPassSlider />

          {/* TP Fund Widget */}
          {features.TP_FUND_WIDGET_ENABLED && (
            <div className="checkout__widget">
              <TpFundWidget />
            </div>
          )}
        </Spacing>
        <InsuranceSection
          enablesInsurance={enablesInsurance}
          wantsInsurance={wantsInsurance}
          hasIneligibleDoc={disableInsuranceSwitch}
          onToggle={this.toggleWantsInsurance}
          isLoading={updatingInsurance}
          insuranceData={{
            message: insuranceMessage,
            price: insuranceAmount,
            singlePrice: insuranceAmountIsUnitPrice && !showMessageNotInsuranceOpenTicket,
            plusTaxes: insuranceAmountIsUnitPrice && !features.INSURANCE_ON_CHECKOUT_ONLY,
            fullPrice: insuranceAmountWithTaxes,
          }}
          t={t}
        />
        {showMessageNotInsuranceOpenTicket && (
          <>
            <OpenTicketMessage isLoggedIn={Boolean(user)} />
            <InfoText>{t('payment:text.insurance_not_available_for_open_ticket')}</InfoText>
          </>
        )}
        {enablesCarbon && (
          <AncillariesSwitch
            isLoading={updatingCarbonOffset}
            id="carbon-footprint"
            title={carbonOffset?.title}
            icon={carbonOffset?.imageUrl}
            message={
              <Trans
                components={{
                  strong: <strong />,
                }}
              >
                {carbonOffset?.description}
              </Trans>
            }
            linkText={t('purchase:link.see_more')}
            href={carbonOffset?.urls}
            onChange={this.toggleWantsCarbonOffset}
            checked={wantsCarbonOffset}
            price={carbonOffset?.amount}
          />
        )}
        {!isExchange && <DiscountCode />}
      </Spacing>
    );

    return (
      <>
        {shouldRenderLoading ? <LoadingWallet /> : null}
        <div
          className={`checkout-container ${isLoading ? 'disabled' : ''}`}
          style={{ display: shouldRenderLoading ? 'none' : 'block' }}
        >
          <Spacing vertical>
            <Spacing size="XXL" vertical responsiveSize="L">
              {bulkTicket && <PassengerSelectorBox />}

              <ReturnCTA type="card" />
              {!isExchange && features.PROMOTIONAL_CODE_ENABLED && <PromotionalCode />}

              <DynamicPriceModal />

              {showWelcomeDigitalDiscountBanner && (
                <Visibility type="showForMobileOnly">
                  <WelcomeDigitalDiscountModal screen="checkout" />
                </Visibility>
              )}

              {onlyDiscount ? (
                <Visibility type="hideForLargeOnly">{servicesComponents}</Visibility>
              ) : (
                <>{!hideServices && <>{servicesComponents}</>}</>
              )}

              {/* Show WalletNotAvailable when user is logged in but wallet is not available for route */}
              {showWalletNotAvailable && (
                <WalletNotAvailable
                  loyalty={
                    env.travelpassLoyaltyConfig?.prefix ||
                    (selectedLoyaltyProgram === 'travelpass' ? 'travelpass' : 'costapass')
                  }
                />
              )}
              {(!isUpdating || purchaserFormInCheckoutStep) && (
                <PaymentOptions
                  availablePayments={availablePayments}
                  paymentMethods={paymentMethods}
                  submitPurchaser={this.onSubmitPurchaser}
                  onSubmitPurchaserFail={this.handlePurchaserSubmitFail}
                  submitCard={this.cardPaymentAttempt}
                  submitCardFail={this.handleFormErrors}
                  submitTransfer={this.onSubmitTransferForm}
                  submitYape={this.handleYapePayment}
                  onYapeFormDataChange={this.handleYapeFormDataChange}
                  onYapeValidationChange={this.handleYapeValidationChange}
                  isYapeSubmitting={isYapeSubmitting}
                  selectedOption={selectedPaymentOption}
                  onPaymentTypeChange={this.setPaymentType}
                  isOpenTicket={openTicket}
                  cardErrorOccured={cardErrorOccured}
                  hideOptions={total === 0}
                  showPurchaserForm={features.PURCHASER_FORM_ON === 'CHECKOUT'}
                  isExchange={isExchange}
                  removeTopMargin={onlyDiscount}
                  onSubmitForm={onSubmitForm}
                  purchaserUpdated={purchaserUpdated}
                  onPaymentButtonClick={this.paymentButtonClick}
                />
              )}
              {features.CAN_USE_POINTS &&
                walletType !== 'doters' &&
                walletType !== 'travelpass' &&
                walletType !== 'costapass' &&
                user?.email && <CheckoutWallet />}
              {user && <SSOAuthDialog />}

              <BookingReview
                departureDate={departs.departure}
                departureTerminal={departs.origin.name}
                departureCity={departs.origin.cityName}
                destinationTerminal={departs.destination.name}
                destinationCity={departs.destination.cityName}
                returnDate={returns.departure}
                passengers={passengers}
                isDepartureOpen={wayIsOpenTicket(departs)}
                isReturnOpen={wayIsOpenTicket(returns)}
              />
            </Spacing>

            {features.SERVICE_LEVELS_ENABLED && <ServiceLevels />}

            {features.POLICIES_DISCLAIMER_LOCATION === 'CHECKOUT' &&
              (showAgreementCheckboxFields ? (
                <AgreementCheckboxFields />
              ) : (
                <PassengerPolicies location="checkout" />
              ))}
            {showRedeemModal && <RedeemHandler />}
            <DotersModalOtp userEmail={user?.email} visible={showOTPModal} isLoading={loadingOTP} />
            <DotersModalUsage
              visible={showDotersModal}
              walletPointsUsed={parseFloat(walletTotalPointsUsed) || parseFloat(walletPointsUsed)}
              walletBalanceUsed={
                parseFloat(walletTotalBalanceUsed) || parseFloat(walletBalanceUsed)
              }
              handleClick={this.handleDotersUsageModalButtonClick}
              currency={env.currency}
            />

            {showNotWsSandboxWarning && (
              <NotWsSandboxWarning onDismiss={this.handleDismissNotWsSandboxWarning} />
            )}

            <CheckoutNavigation leftContent={showLegacyInstallmentNotice && installmentNotice}>
              {!isUpdating && !shouldHideMainPaymentButton && (
                <PaymentButton
                  paymentEngine={paymentEngine}
                  paymentType={paymentType}
                  totalPrice={selectedPaymentTotal}
                  onClick={this.paymentButtonClick}
                  isExchange={isExchange}
                  isLoading={buttonLoadingWithCardCheckout}
                  isDisabled={disabledPaymentButton}
                  isPointsPaying={isPointsPaying}
                  walletText={this.getWalletPointsText()}
                />
              )}
            </CheckoutNavigation>
          </Spacing>
        </div>
      </>
    );
  }
}

Checkout.propTypes = propTypes;
Checkout.contextType = MercadoPagoTDCContext;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
  env: state.whitelabelConfig.env,
  dispatch: state.dispatch,
  cardCheckoutMode: selectCardCheckoutDecision(state, state.purchase.get('selectedPaymentMethod'))
    .mode,
});

export default connect(mapStateToProps)(
  withTranslation('payment')(withCieloTDCContext(withInsuranceEligibility(Checkout))),
);
